/**
 * Length (Regular / Tall) selector behaviour.
 *
 * Lives here rather than inline in snippets/length-selector.liquid because the
 * control is also injected into the quick add sheet on collection pages, where
 * that snippet's inline script never runs. Binding is delegated from document,
 * so markup that appears later works without re-running anything.
 */
(function () {
  var SCROLL_STORAGE_KEY = 'kalea-length-scroll-y';

  /**
   * Browsers restore radio checked-state on back/forward by DOM position,
   * independent of the HTML attribute, in a pass that can run after inline
   * scripts. Re-assert the state that matches this page, now and on pageshow
   * (the only handler that runs when a page is thawed from bfcache).
   */
  function enforceCorrectLengthChecked() {
    document.querySelectorAll('.kalea-length-option__radio').forEach(function (radio) {
      radio.checked = radio.hasAttribute('data-kalea-current');
    });
  }

  enforceCorrectLengthChecked();
  window.addEventListener('pageshow', enforceCorrectLengthChecked);

  /**
   * Switching Length means loading a different product (Regular and Tall are
   * separate products), so a reload is unavoidable -- but the scroll position
   * is kept. A head script in layout/theme.liquid restores it before paint.
   */
  function navigateTo(url) {
    try {
      sessionStorage.setItem(SCROLL_STORAGE_KEY, String(window.scrollY));
    } catch (e) {
      /* private mode: the jump is cosmetic, carry on */
    }
    window.location.href = url;
  }

  /**
   * Which quick add button opened the sheet. Needed to re-render it for the
   * paired product: the component owns the fetch and the render, and the
   * sheet itself is shared, so there is no way back to the card from it.
   */
  var lastQuickAdd = null;

  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!(target instanceof Element)) return;
      var button = target.closest('.quick-add__button');
      if (button) lastQuickAdd = button.closest('quick-add-component');
    },
    true
  );

  /** Pages already fetched, so a hover or a second click costs nothing. */
  var pageCache = {};

  function fetchPage(url) {
    var key = url.split('?')[0];
    if (!pageCache[key]) {
      pageCache[key] = fetch(url)
        .then(function (r) {
          return r.text();
        })
        .catch(function () {
          delete pageCache[key];
          return null;
        });
    }
    return pageCache[key];
  }

  /** Product JSON per path, used to re-point the back-in-stock app. */
  var productCache = {};

  function fetchProductData(url) {
    var key = url.split('?')[0];
    if (!productCache[key]) {
      productCache[key] = fetch(key + '.js')
        .then(function (r) {
          return r.ok ? r.json() : null;
        })
        .catch(function () {
          delete productCache[key];
          return null;
        });
    }
    return productCache[key];
  }

  /** First gallery images already requested, keyed by path. */
  var imageCache = {};

  /**
   * The swapped-in gallery starts with no image decoded, so for a few frames
   * the media column has no height: the page collapses, the browser keeps the
   * scroll offset, and whatever sits further down flashes into view before
   * the image lands and pushes it back. Requesting the first image up front
   * means it is already in the cache when the markup arrives.
   *
   * The same srcset and sizes are used as the real img, so the browser picks
   * the same candidate and the swap is a cache hit rather than a second
   * download.
   */
  function preloadFirstImage(url, doc) {
    var key = url.split('?')[0];
    if (imageCache[key]) return imageCache[key];

    var source = doc.querySelector('[data-product-grid-content] img');
    if (!source) return Promise.resolve();

    imageCache[key] = new Promise(function (resolve) {
      var image = new Image();
      image.decoding = 'async';
      image.fetchPriority = 'high';
      var sizes = source.getAttribute('sizes');
      var srcset = source.getAttribute('srcset');
      if (sizes) image.sizes = sizes;
      if (srcset) image.srcset = srcset;
      image.src = source.getAttribute('src') || '';
      if (image.complete) {
        resolve();
        return;
      }
      image.addEventListener('load', function () {
        resolve();
      });
      image.addEventListener('error', function () {
        resolve();
      });
      // However it goes, the swap is never held up for long.
      setTimeout(resolve, 600);
    });

    return imageCache[key];
  }

  /**
   * Holds the swapped area at the height it had until the new gallery has
   * something to show, so the page cannot collapse under the scroll position.
   */
  function holdHeight(node, height) {
    node.style.minHeight = height + 'px';

    var release = function () {
      node.style.removeProperty('min-height');
    };

    // Whatever happens to the image, the lock always comes off.
    setTimeout(release, 1500);

    var image = node.querySelector('img');
    if (!image || image.complete) {
      requestAnimationFrame(release);
      return;
    }

    image.addEventListener('load', release, { once: true });
    image.addEventListener('error', release, { once: true });
  }

  /**
   * The back-in-stock app reads the product it watches from window.REZ_DATA,
   * which its app embed renders once per page load. After a Length swap that
   * global still describes the product we came from, so the app does not
   * recognise the variant ids now on the page and builds no Notify me button
   * for a sold-out size. Pointing it at the new product is enough -- it
   * re-reads the global on every check.
   */
  async function syncBackInStockData(url) {
    var data = window.REZ_DATA;
    if (!data || !data.current_product) return;

    try {
      var product = await fetchProductData(url);
      if (!product || !product.variants) return;

      var available = {};
      product.variants.forEach(function (variant) {
        available[variant.id] = variant.available;
      });

      data.current_product = {
        id: product.id,
        title: product.title,
        handle: product.handle,
        tags: product.tags,
        variants: product.variants.map(function (variant) {
          return {
            id: variant.id,
            title: variant.title,
            available: variant.available,
            price: variant.price,
          };
        }),
      };
      data.availableByVariantId = available;
      // Stock counts are not in the product JSON. Emptying the map makes the
      // app fall back to the availability flags above instead of trusting the
      // previous product's numbers.
      data.inventoryQuantityByVariantId = {};
      data.current_product_tags = product.tags;

      var variantId = new URL(url, window.location.origin).searchParams.get('variant');
      if (variantId) data.current_variant_id = Number(variantId);
    } catch (e) {
      /* the app keeps its old data; everything else on the page still works */
    }
  }

  /**
   * That same app only compares the url against the one it last saw from
   * inside a MutationObserver on <body>, so a history rewrite on its own is
   * invisible to it. A throwaway node is the signal.
   */
  function pokeBackInStockObserver() {
    var probe = document.createElement('div');
    probe.style.display = 'none';
    document.body.appendChild(probe);
    probe.remove();
  }

  /**
   * Once the back-in-stock app has built its button it keeps a reference to
   * that one node and only ever toggles its display. Swapping the page out
   * from under it leaves the node detached, and the app goes on hiding and
   * showing something nobody can see. Carrying the node across the swap keeps
   * its reference valid.
   */
  function carryWatchButton(watchButton, next) {
    if (!watchButton) return;

    var position =
      typeof window.rezSupportBisBtnPosition === 'function' ? window.rezSupportBisBtnPosition() : null;
    var selectors = Array.isArray(position) ? position : [position || '.product-form-buttons'];

    for (var i = 0; i < selectors.length; i++) {
      var anchor = next.querySelector(selectors[i]);
      if (anchor && anchor.parentNode) {
        anchor.parentNode.insertBefore(watchButton, anchor.nextSibling);
        return;
      }
    }
  }

  /**
   * Swaps the product page over to the paired product without a reload.
   *
   * Only what the page renders itself is swapped, so anything an app owns is
   * re-pointed by hand afterwards. A target variant that is sold out still
   * falls back to a normal navigation, so every app on the page renders for
   * the right product from the start.
   */
  async function swapProductPage(url) {
    var current = document.querySelector('[data-product-grid-content]');
    if (!current) {
      navigateTo(url);
      return;
    }

    // Both requests go out together: the app has to be re-pointed before the
    // swap, or its observer wakes on our own mutation and reads stale data.
    var pending = syncBackInStockData(url);
    var html = await fetchPage(url);
    if (!html) {
      navigateTo(url);
      return;
    }
    await pending;

    try {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var next = doc.querySelector('[data-product-grid-content]');
      if (!next) {
        navigateTo(url);
        return;
      }

      await preloadFirstImage(url, doc);

      var watchButton = current.querySelector('.rez-button-wrapper');
      var height = current.getBoundingClientRect().height;

      current.replaceWith(next);
      holdHeight(next, height);
      carryWatchButton(watchButton, next);

      // The sticky buy bar sits outside the swapped area and would otherwise
      // keep offering the previous product.
      var sticky = document.querySelector('sticky-add-to-cart');
      var nextSticky = doc.querySelector('sticky-add-to-cart');
      if (sticky && nextSticky) sticky.replaceWith(nextSticky);

      // Judge.me renders its star badge into markup that has just been
      // replaced; it exposes a re-render for exactly this.
      if (window.jdgm && typeof window.jdgm.batchRenderBadges === 'function') {
        window.jdgm.batchRenderBadges();
      }

      document.title = doc.title;
      history.replaceState({}, '', url);
      enforceCorrectLengthChecked();
      pokeBackInStockObserver();
    } catch (e) {
      navigateTo(url);
    }
  }

  /**
   * Swaps the sheet over to the paired product instead of navigating to it.
   * Falls back to a normal page load if anything is missing, so picking a
   * length never dead-ends.
   */
  async function swapSheetToProduct(url) {
    if (!lastQuickAdd || typeof lastQuickAdd.fetchProductPage !== 'function') {
      navigateTo(url);
      return;
    }

    try {
      var doc = await lastQuickAdd.fetchProductPage(url);
      var grid = doc && doc.querySelector('[data-product-grid-content]');
      if (!grid) {
        navigateTo(url);
        return;
      }

      var title = doc.querySelector('.product-details h1');
      if (title) lastQuickAdd.dataset.productTitle = title.textContent.trim();

      await lastQuickAdd.updateQuickAddModal(grid.cloneNode(true));

      // The sheet's title link is built from the card's own product url, so
      // after the swap it still points at Regular. The variant lookup reads
      // the product from that link, so it has to follow the swap or the
      // sheet adds Regular's variants.
      var link = document.querySelector('#quick-add-modal-content .product-header a');
      if (link) link.setAttribute('href', url.split('?')[0]);
    } catch (e) {
      navigateTo(url);
    }
  }

  ['pointerenter', 'touchstart'].forEach(function (type) {
    document.addEventListener(
      type,
      function (event) {
        var target = event.target;
        if (!(target instanceof Element)) return;
        var label = target.closest('.kalea-length-option__button');
        if (!label) return;
        var radio = label.querySelector('.kalea-length-option__radio');
        var href = radio && radio.getAttribute('data-paired-product-url');
        if (!href) return;

        fetchProductData(href);
        fetchPage(href).then(function (html) {
          if (!html) return;
          preloadFirstImage(href, new DOMParser().parseFromString(html, 'text/html'));
        });
      },
      { capture: true, passive: true }
    );
  });

  document.addEventListener('change', function (event) {
    var radio = event.target;
    if (!(radio instanceof HTMLInputElement)) return;
    if (!radio.classList.contains('kalea-length-option__radio')) return;

    var fallbackHref = radio.getAttribute('data-href');
    if (!fallbackHref) return;

    var inSheet = !!radio.closest('#quick-add-modal-content');

    var wrapper = radio.closest('.kalea-length-option-wrapper');
    var dataScript = wrapper && wrapper.querySelector('[data-kalea-length-paired-variants]');
    var pairedProductUrl = radio.getAttribute('data-paired-product-url');

    // Scoped to the sheet when the control sits inside it: a collection page
    // has many variant pickers, and the page's own would otherwise win.
    var root = radio.closest('#quick-add-modal-content') || document;
    var colorInput = root.querySelector('variant-picker input[name^="Color-"]:checked');
    var sizeInput = root.querySelector('variant-picker input[name^="Size-"]:checked');

    function go(url, available) {
      if (inSheet) {
        swapSheetToProduct(url);
      } else if (available) {
        swapProductPage(url);
      } else {
        // Sold out, or availability unknown: let the page load so every app
        // on it renders for the right product.
        navigateTo(url);
      }
    }

    if (!dataScript || !colorInput || !sizeInput) {
      go(fallbackHref, false);
      return;
    }

    var variants;
    try {
      variants = JSON.parse(dataScript.textContent);
    } catch (e) {
      go(fallbackHref, false);
      return;
    }

    var currentColor = colorInput.value;
    var currentSize = sizeInput.value;

    var exactMatch = variants.find(function (v) {
      return v.color === currentColor && v.size === currentSize;
    });
    // First the same colour in stock, and only then the same colour at all:
    // picking the first variant of the colour could land the shopper on a
    // sold-out size when another size of it is available.
    var colorMatch =
      variants.find(function (v) {
        return v.color === currentColor && v.available;
      }) ||
      variants.find(function (v) {
        return v.color === currentColor;
      });

    if (exactMatch && exactMatch.available) {
      go(exactMatch.url, true);
    } else if (colorMatch) {
      go(colorMatch.url, colorMatch.available === true);
    } else if (pairedProductUrl) {
      go(pairedProductUrl, false);
    } else {
      go(fallbackHref, false);
    }
  });
})();
